/*
 * Copyright (c) 2019 Honeywell International, Inc. All rights reserved.
 *
 * This file contains trade secrets of Honeywell International, Inc.  No part
 *  may be reproduced or transmitted in any form by any means or for any
 *  purpose without the express written permission of Honeywell.
 *
 */

import SiteSelectionStore from "./SiteSelectionStore";
import GatewayRegistrationStore from "./GatewayRegistrationStore";
import GlobalStore from "../GlobalStore/GlobalStore";
import {AbstractWizardState} from "../../components/state/AbstractWizardState";
import GatewayValidationStore from "./GatewayValidationStore";
import {LogoutFunction} from "../../components/common/ForgeConnectIdentity";
import GatewayRegistration from '../../components/service/gatewayRegistration/GatewayRegistration'
import { IGatewayDetails} from "../../components/gateway/registration/IGatewayDetails"

/**
 * The details of the logged in user
 */
export interface ILoggedInUserDetails {

  firstName: string;

  lastName: string;

  userPrincipalName: string;
}
/**
 * The interface for the customer details.
 */
export interface IOrganizationDetails {

  /**
   * The id for the customer
   */
  organizationId: string;

  /**
   * Customer name
   */
  organizationName?: string;

  /**
   * Address 1
   */
  address1?: string;

  /**
   * Address 2
   */
  address2?: string;
}

/**
 * The interface for the site details
 */
export interface ISiteDetails {

  /**
   * The id for the site
   */
  siteId: string;

  /**
   * The name of the site
   */
  siteName: string;

  /**
   * The name of gateway
   */
  gatewayName?: string;

  /**
   * The address line # 1 for the site
   */
  address1?: string;

  /**
   * The address line # 2 for the site
   */
  address2?: string

  /**
   * The details of the cutomer to which the site belongs
   */
  customerDetails: IOrganizationDetails
}

/**
 * Gateway RegistrationProgress inputs
 */
export interface IRegistrationDetails {

  /**
   * Serial Number of the gateway
   */
  serialNumber: string;

  /**
   * Original end point name of the gateway
   */
  epName: string

  /**
   * Enrollment Id of the gateway (In the first-to-claim case)
   */
  enrollmentId?: string;

  /**
   * Device Id of the gateway
   */
  deviceId?: string
}

/**
 * The properties for the DIY component
 */
export interface IDIYGatewayManagementProps {
  logout?: LogoutFunction;

  userInfo?: ILoggedInUserDetails;
  /**
   * The gobal store
   */
  globalStore?: GlobalStore

  gatewayValidationStore?: GatewayValidationStore;
}

/**
 * The properties for the DIY component
 */
export interface IFCPDIYGatewayManagementProps {
  logout?: LogoutFunction;

  /**
   * The info about the logged in user.
   */
   userInfo?: ILoggedInUserDetails;
  /**
   * The gobal store
   */
  globalStore?: GlobalStore
}


/**
 * The properties for gateway registration component
 */
export interface IDIYGatewayManagementState extends IRegistrationDetails{

  /**
   * The status of vaidation for gateway registration inputs
   */
  validationStatus: GatewayPropertiesValidationStatus;

  /**
   * Flag to indicate if the user context has been initialized for feature flag
   */
  featureFlagUserContextInitialized: boolean;

  /**
   * The logged in user's first name
   */
  firstName: string;

  /**
   * The logged in user's last name
   */
  lastName: string;

  /**
   * The selected site details
   */
  selectedSite: ISiteDetails;

  /**
   * The state of gateway vis`-s`-vis` registration
   */
  registrationStatus: AbstractWizardState;

}

/**
 * Props for the gateway registration component
 */
export interface IGatewayRegistrationProps extends  IRegistrationDetails {

  /**
   * Site for which the registration needs to be done
   */
  siteDetails: ISiteDetails;

  /**
   * gateway registration successful
   */
  gatewayRegistrationSuccess?: () => void;

  /**
   *  gateway registration failed
   */
  gatewayRegistrationFailed?: (status: number) => void;
  /**
   * The gateway registration store
   */
  gatewayRegistrationStore?: GatewayRegistrationStore

}

export interface IGatewayRegistrationSummaryProps extends IRegistrationDetails {
  /**
   * The details of the site for which the gateway is registered/
   */
  registeredSite: ISiteDetails;

  /**
   * This will be needed for Mobile views where the link is always shown in
   * the content area.
   * @returns {{}}
   */
  onContactSupport?: () => void;
}


/**
 * The properties for the Site Details component
 */
export interface ISiteSelectionProps {

  /**
   * The serial number for display purposes
   */
  serialNumber: string;
  /**
   * The selected site id
   */
  selectedSite?: string;

  /**
   * Callback for site selected and further progress
   * @returns {{}}
   */
  siteSelected: (siteDetails: ISiteDetails) => void;
  /**
   * The mobx store for Site Selection component
   */
  siteSelectionStore?: SiteSelectionStore;
}

/**
 * The state for the Site Details component
 */
export interface ISiteSelectionState {
  selectedSite: ISiteDetails;
  sitesLoaded: boolean;
  seekConfirmation: boolean;
  errors: any;
}

/**
 * The state for the Gateway Details component
 */
 export interface IGatewayRegistration {  
  selectedGateway:ISelectedGateway;
  sitesLoaded: boolean;
  errors: any;
  renderView: boolean;
  
}

/**
 * The state for the selected Gateway Details component
 */
 export interface ISelectedGateway {  
  gatewayName: string;
  tenant: string;
  serialNumber: string;
  enrollmentIdentity: string;
  originalEp: string;
  projectId: string;  
  gatewayId: string;
}

/**
 * Gateway Registration setup state 
 */
export interface IGatewayRegistrationSetup {  
  isSuccess:boolean;
  isLoading:boolean;
  errors: IAxiosError;  
}

/**
 * axios error details
 */
export interface IAxiosError {
  message:string;
  httpStatus:string;
  statusCode:number;
}
/**
 * Props for the gateway registration 
 */
 export interface IGatewayRegProps{
  /**
   * The gateway registration service
   */
  gatewayRegistration?: GatewayRegistration;
  selectedGateway:ISelectedGateway;
  gatewayDetails:IGatewayDetails;

}


/**
 * The status of the gateway registration input validation
 */
export enum GatewayPropertiesValidationStatus {
  SUCCESS,
  INVALID_SERIAL_NUMBER,
  INVALID_ENDPOINT_NAME,
  INVALID_ENROLLMENT_ID,
  INVALID_DEVICE_ID,
  PRE_REG_GATEWAYS_NOT_ALLOWED,
  UNKNOWN
}

/**
 * The registration state values
 */
export enum RegistrationState {
  NOTSTARTED,
  INPROGRESS,
  COMPLETE,
  FAILED
}

